import { z } from "zod";
import { idSchema } from "./common.js";

/** Every action the platform records; the web client carries a label per key. */
export const auditActionSchema = z.enum([
  "auth.signed_in",
  "auth.signed_out",
  "auth.session_revoked",
  "auth.email_verified",
  "auth.password_changed",
  "auth.password_reset",
  "auth.totp_enabled",
  "auth.totp_disabled",
  "auth.passkey_added",
  "auth.passkey_removed",
  "invite.created",
  "invite.resent",
  "invite.revoked",
  "invite.accepted",
  "member.role_changed",
  "member.ownership_transferred",
  "member.suspended",
  "member.reactivated",
  "member.sessions_revoked",
  "member.mfa_reset",
  "member.password_reset_sent",
  "dns.email_setup_applied",
  "order.created",
  "order.paid",
  "domain.registered",
  "domain.registration_failed",
  "domain.renewed",
  "domain.auto_renew_failed",
  "domain.auto_renew_changed",
  "domain.settings_changed",
  "domain.nameservers_changed",
  "domain.auth_code_viewed",
  "domain.connected",
  "domain.verified",
  "domain.disconnected",
  "mailbox.created",
  "mailbox.deleted",
  "mailbox.password_reset",
  "mailbox.alias_added",
  "mailbox.alias_removed",
  "mail_group.created",
  "mail_group.updated",
  "mail_group.deleted",
  "billing.plan_chosen",
  "workspace.exported",
  "workspace.closed",
  "workspace.renamed",
  "workspace.sending_paused",
  "workspace.sending_resumed",
  "workspace.queued_mail_released",
  "workspace.queued_mail_cancelled",
  "account.exported",
  "billing.subscription_changed",
]);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const auditEventSchema = z.object({
  id: idSchema,
  action: auditActionSchema,
  actor: z.object({ id: idSchema.nullable(), label: z.string() }).nullable(),
  target: z
    .object({ type: z.string(), id: z.string().nullable(), label: z.string().nullable() })
    .nullable(),
  details: z.record(z.string(), z.unknown()),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditQuerySchema = z.object({
  /** Events strictly older than this instant; taken from the previous page's nextCursor. */
  before: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Action key or a dotted prefix such as "member." */
  action: z.string().max(60).optional(),
  actorId: idSchema.optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export const auditListSchema = z.object({
  events: z.array(auditEventSchema),
  nextCursor: z.iso.datetime().nullable(),
});
export type AuditList = z.infer<typeof auditListSchema>;
